import { toast } from 'react-toastify';
import axios from 'axios';
import _mapKeys from 'lodash/mapKeys';
import _camelCase from 'lodash/camelCase';
import _size from 'lodash/size';

import { TOASTS_TEMPLATES } from 'constants/toastTypes';
import ErrorHandler from 'store/utils/ErrorHandler';
import downloadFile from 'store/utils/downloadFile';
import Notify from 'store/utils/Notify';
import promiseWithMinimunTime from 'store/utils/promiseWithMinimumTime';

import { RESET_PROJECTS_PAGINATION_PARAMS } from 'store/services/projects/actionTypes';
import { getNotificationsCount } from 'store/services/notifications/actions';
import getFeatures from 'store/services/features/actions';
import { getSceneNameSelector } from 'store/services/router/selectors';
import { calculateExportToastTitle } from 'store/utils/project';
import { projectRequest } from 'store/utils/request';
import * as api from './api';
import * as types from './actionTypes';

import { getProject, getFinalPurgableData } from './selectors';
import { getEntitiesList } from '../entities/actions';
import { getRuleGroupsList } from '../ruleGroups/actions';
import { getIntentsList } from '../intents/actions';
import { getDictionariesList } from '../dictionaries/actions';
import { getContextsList } from '../contexts/actions';

/**
 * Get purges list
 */
export const getPurgeProjects = () => async dispatch => {
  try {
    await dispatch({
      type: types.GET_PURGE_PROJECTS_REQUEST,
    });

    const { data } = await api.getPurgeProjectsRequest();
    await dispatch({
      type: types.GET_PURGE_PROJECT_SUCCESS,
      payload: {
        purgeList: data,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_PURGE_PROJECT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const closePurgeModal = () => ({
  type: types.CLOSE_PURGE_MODAL,
});

/**
 * Get a project
 *
 * @param projectAccount
 * @param projectSlug
 */

export const getAndSelectProject = (
  projectAccount,
  projectSlug,
) => async dispatch => {
  try {
    dispatch({
      type: types.GET_PROJECT_REQUEST,
    });
    const { data } = await api.getProjectRequest(projectAccount, projectSlug);

    dispatch({
      type: types.GET_PROJECT_SUCCESS,
    });

    dispatch({
      type: types.SELECT_PROJECT,
      payload: {
        project: data,
      },
    });

    dispatch(getFeatures());
  } catch (error) {
    dispatch({
      type: types.GET_PROJECT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

// This action is used only be purgeProject action
// We made it a different action for making testing easier
export const updateSceneListIfNeeded = purgableData => (dispatch, getState) => {
  const state = getState();
  const sceneName = getSceneNameSelector(state);

  switch (sceneName) {
    case 'entities':
      if (_size(purgableData.entities) > 0) {
        dispatch(getEntitiesList({ page: 1 }, {}, true));
      }
      break;

    case 'groups':
      if (
        _size(purgableData.rules) > 0 ||
        _size(purgableData.rule_groups) > 0
      ) {
        dispatch(getRuleGroupsList({ page: 1 }, {}, true));
      }
      break;

    case 'intents':
      if (_size(purgableData.intents) > 0) {
        dispatch(getIntentsList({ page: 1 }, {}, true));
      }
      break;

    case 'dictionaries':
      if (_size(purgableData.dictionaries) > 0) {
        dispatch(getDictionariesList({ page: 1 }, {}, true));
      }
      break;
    case 'contexts':
      if (_size(purgableData.contexts) > 0) {
        dispatch(getContextsList({ page: 1 }, {}, true));
      }
      break;
    default:
  }
};

/**
 * Purge project items
 */
export const purgeProject = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.PURGE_PROJECT_REQUEST,
    });

    const state = getState();
    const purgableData = getFinalPurgableData(state);

    await api.purgeProjectRequest(purgableData);

    dispatch({
      type: types.PURGE_PROJECT_SUCCESS,
    });

    dispatch({
      type: types.PURGE_ACTIONS,
      payload: {
        data: purgableData,
      },
    });

    // If we are in entities, rules, intents, dictionaries page
    // We should reload the list if needed in order to have correct ordering
    dispatch(updateSceneListIfNeeded(purgableData));

    Notify.success({
      title: 'Project items were purged successfully',
    });
  } catch (error) {
    dispatch({
      type: types.PURGE_PROJECT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to purge Project items',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

/**
 * Select a project
 *
 * @param project
 */
export const selectProject = project => dispatch => {
  dispatch({
    type: types.SELECT_PROJECT,
    payload: {
      project,
    },
  });

  dispatch({
    type: RESET_PROJECTS_PAGINATION_PARAMS,
  });

  dispatch(getNotificationsCount());
};

/**
 * Export current project
 */
const exportProjectRequestId = 'export-project';
export const exportProject = (data = {}, triggerNotifiers = true) => async (
  dispatch,
  getState,
  /* eslint-disable-next-line consistent-return */
) => {
  let toastId;
  try {
    await dispatch({
      type: types.EXPORT_PROJECT_REQUEST,
    });

    if (triggerNotifiers) {
      toastId = Notify.success({
        title: calculateExportToastTitle('Export Started', data.level),
        description:
          'Your data is being exported now. When the export is complete, the download will start automatically',
        template: TOASTS_TEMPLATES.PRELOADER,
      });
    }

    const { name } = getProject(getState());

    const THREE_SEC_IN_MILLISEC = 3 * 1000;

    const exportWithMinTime = promiseWithMinimunTime(
      api.exportProjectRequest,
      THREE_SEC_IN_MILLISEC,
    );

    const response = await exportWithMinTime(data, {
      requestId: exportProjectRequestId,
    });

    await dispatch({
      type: types.EXPORT_PROJECT_SUCCESS,
    });

    await downloadFile(response.data, `${name}.zip`);

    if (triggerNotifiers) {
      toast.dismiss(toastId);
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return Notify.success({
        title: 'Export was canceled successfully',
      });
    }

    await dispatch({
      type: types.EXPORT_PROJECT_ERROR,
      payload: {
        error: error.message,
      },
    });

    if (triggerNotifiers) {
      // We need to dismiss the "preloader" toast
      // if sth goes wrong during the export process
      toast.dismiss(toastId);

      Notify.error({
        title: calculateExportToastTitle('Export failed', data.level),
        description:
          'Failed to export this file. Please check the log files for more details',
      });
    } else {
      throw error;
    }
  }
};

/**
 * Cancel export project
 */
export const cancelExportProject = () => dispatch => {
  projectRequest.cancel(exportProjectRequestId);
  dispatch({
    type: types.CANCEL_EXPORT_PROJECT,
  });
};

export const getGeneralProjectSettingsRequest = () => ({
  type: types.GET_GENERAL_PROJECT_SETTINGS_REQUEST,
});

export const getGeneralProjectSettingsSuccess = projectSettings => ({
  type: types.GET_GENERAL_PROJECT_SETTINGS_SUCCESS,
  payload: projectSettings,
});

export const getGeneralProjectSettingsError = error => ({
  type: types.GET_GENERAL_PROJECT_SETTINGS_ERROR,
  payload: error,
});

export const getGeneralProjectSettings = () => async dispatch => {
  try {
    dispatch(getGeneralProjectSettingsRequest());
    const response = await api.getGeneralProjectSettingsRequest();
    const responseDataFormatted = _mapKeys(response.data, (value, key) =>
      _camelCase(key),
    );
    dispatch(getGeneralProjectSettingsSuccess(responseDataFormatted));
  } catch (error) {
    const errorMessage = ErrorHandler.getNotificationError(error);

    dispatch(getGeneralProjectSettingsError(errorMessage));
    Notify.error({
      title: 'An error occurred',
      description: errorMessage,
    });
  }
};

export const updateProjectName = (account, nameToApply) => async dispatch => {
  try {
    dispatch({
      type: types.UPDATE_PROJECT_NAME_REQUEST,
    });
    const response = await api.updateProjectNameRequest({
      name: nameToApply,
      account,
    });

    await dispatch({
      type: types.UPDATE_PROJECT_NAME_SUCCESS,
      payload: response.data,
    });

    Notify.success({
      title: 'Project name was updated successfully',
    });
  } catch (error) {
    const errorMessage = ErrorHandler.getNotificationError(error);

    dispatch({
      type: types.UPDATE_PROJECT_NAME_ERROR,
      payload: errorMessage,
    });

    Notify.error({
      title: 'An error occurred',
      description: errorMessage,
    });
  }
};

export const getProjectSnapshots = () => async dispatch => {
  try {
    dispatch({
      type: types.GET_PROJECT_SNAPSHOTS_REQUEST,
    });
    const { data } = await api.getProjectSnapshotsRequest();
    const dataFormatted = _mapKeys(data, (value, key) =>
      key === 'results' ? 'items' : key,
    );

    dispatch({
      type: types.GET_PROJECT_SNAPSHOTS_SUCCESS,
      payload: dataFormatted,
    });
  } catch (error) {
    const errorMessage = ErrorHandler.getNotificationError(error);

    dispatch({
      type: types.GET_PROJECT_SNAPSHOTS_ERROR,
      payload: errorMessage,
    });

    Notify.error({
      title: 'An error occurred',
      description: errorMessage,
    });
  }
};

export const createProjectSnapshot = (
  nextSnapshotName,
  projectWithSnapshotName,
  userFullNameDotFormat,
) => async dispatch => {
  try {
    dispatch({
      type: types.CREATE_PROJECT_SNAPSHOT_REQUEST,
    });

    await api.createProjectSnapshotRequest({
      name: nextSnapshotName,
    });

    dispatch({
      type: types.CREATE_PROJECT_SNAPSHOT_SUCCESS,
    });

    Notify.success({
      title: `Snapshot ready!`,
      description: `A snapshot of the project "${projectWithSnapshotName}" has been taken by ${userFullNameDotFormat}.`,
    });
  } catch (error) {
    const errorMessage = ErrorHandler.getNotificationError(error);

    dispatch({
      type: types.CREATE_PROJECT_SNAPSHOT_ERROR,
      payload: errorMessage,
    });

    Notify.error({
      title: 'An error occurred',
      description: errorMessage,
    });
  }
};

export const downloadProjectSnapshot = (
  snapshotId,
  projectName,
) => async dispatch => {
  let toastId;
  try {
    dispatch({
      type: types.DOWNLOAD_PROJECT_SNAPSHOTS_REQUEST,
    });
    toastId = Notify.success({
      title: `${projectName} snapshot is downloading now. Please wait...`,
      template: TOASTS_TEMPLATES.PRELOADER,
    });

    const response = await api.downloadProjectSnapshotRequest(snapshotId);

    dispatch({
      type: types.DOWNLOAD_PROJECT_SNAPSHOTS_SUCCESS,
    });
    toast.dismiss(toastId);

    const projectFileName = `${projectName.charAt(0).toUpperCase() +
      projectName.substring(1)}.zip`;
    await downloadFile(response.data, projectFileName);

    Notify.success({
      title: 'Project snapshot was downloaded successfully',
    });
  } catch (error) {
    toast.dismiss(toastId);

    const errorMessage = ErrorHandler.getNotificationError(error);

    dispatch({
      type: types.DOWNLOAD_PROJECT_SNAPSHOTS_ERROR,
      payload: errorMessage,
    });

    Notify.error({
      title: 'An error occurred',
      description: errorMessage,
    });
  }
};

export const rollbackProject = snapshotId => async dispatch => {
  try {
    dispatch({
      type: types.ROLLBACK_PROJECT_REQUEST,
    });

    const response = await api.rollbackProjectRequest(snapshotId);

    dispatch({
      type: types.ROLLBACK_PROJECT_SUCCESS,
      payload: response.data,
    });

    dispatch({ type: types.ROLLBACK_PROJECT });

    Notify.success({
      title: `Project rollback was completed successfully!`,
    });
  } catch (error) {
    const errorMessage = ErrorHandler.getNotificationError(error);

    dispatch({
      type: types.ROLLBACK_PROJECT_ERROR,
      payload: errorMessage,
    });

    Notify.error({
      title: 'An error occurred',
      description: errorMessage,
    });
  }
};

/**
 * Delete a project
 *
 * @param id
 */

export const deleteProject = () => async dispatch => {
  try {
    dispatch({
      type: types.DELETE_PROJECT_REQUEST,
    });

    await api.deleteProjectRequest();

    dispatch({
      type: types.DELETE_PROJECT_SUCCESS,
    });

    Notify.success({
      title: 'Project was deleted successfully',
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_PROJECT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to delete Project',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/*
 * Make a project version control enabled
 */

export const initializeVersionControlRequest = () => ({
  type: types.INITIALIZE_VERSION_CONTROL_REQUEST,
});

export const initializeVersionControlSuccess = data => ({
  type: types.INITIALIZE_VERSION_CONTROL_SUCCESS,
  payload: { data },
});

export const initializeVersionControlError = error => ({
  type: types.INITIALIZE_VERSION_CONTROL_ERROR,
  payload: { error },
});

export const initializeVersionControl = (server, repo, group) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch(initializeVersionControlRequest());
    const { slug: project, account } = getProject(getState());

    const { data } = await api.initializeVersionControl({
      server,
      repo,
      group,
      project,
      account,
    });

    dispatch(initializeVersionControlSuccess(data));

    Notify.success({
      title: data.message,
    });
  } catch (error) {
    dispatch(initializeVersionControlError(error.message));
    Notify.error({
      title: 'Failed to enable Version Control',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const resetProjectBranch = () => dispatch => {
  dispatch({
    type: types.RESET_PROJECT_BRANCH,
  });
};

export const updateProjectBranch = projectBranch => ({
  type: types.UPDATE_PROJECT_BRANCH,
  payload: projectBranch,
});
